import { ParseKeys, TOptions } from 'i18next';
import type { IconName } from '@/components/design-system/Icon';

export enum SortOptions {
    DateAsc = 'date_asc',
    DateDesc = 'date_desc',
    NameAsc = 'title_asc',
    NameDesc = 'title_desc',
    ClosingSoon = 'closing_soon',
    BestMatch = 'best_match',
    PriceAsc = 'price_asc',
    PriceDesc = 'price_desc',
    Popular = 'popular',

    Winning = 'winning',
    Loosing = 'loosing'
}

export enum AdditionalSortOption {
    PlacedAtDesc = 'placed_at_desc',
    PlacedAtAsc = 'placed_at_asc'
}

export enum FilterOptions {
    Winning = 'winning',
    Loosing = 'loosing'
}

export type DisplaySortOptionsType = {
    value: SortOptions | undefined;
    label: `header:${ParseKeys<'header', TOptions, undefined>}`;
    /** The ds sort mark (master 262:3536, plus the two drawn for this menu). */
    icon?: IconName;
};

export type SortDirection = 'asc' | 'desc';

/**
 * The sort menu's rows.
 *
 * `DisplaySortOptions` below is one row per API value, which put Name (A-Z)
 * and Name (Z-A) — the same sort, read in two directions — on two lines, and
 * left the other three with no way to be reversed at all. A field is the
 * thing being sorted; the direction is a separate axis the row carries. Two
 * values collapse to one row, and the row says which way it is pointing.
 *
 * `desc` is what makes a field reversible, and only price and name have a
 * counterpart the API understands: `closing_soon`, `best_match` and `popular`
 * are single-ended, so their rows select but do not flip. Give them a reverse
 * value on the backend and they pick the behaviour up here for free.
 */
export type SortFieldType = {
    id: string;
    label: `header:${ParseKeys<'header', TOptions, undefined>}`;
    icon?: IconName;
    /** The value sent for the field's natural order. */
    asc: SortOptions;
    /** Absent when the API has no reverse for this field. */
    desc?: SortOptions;
};

export const DisplaySortFields: SortFieldType[] = [
    { id: 'closing', label: 'header:search-sort.closing-soon-sort', icon: 'sort-time', asc: SortOptions.ClosingSoon },
    { id: 'best-match', label: 'header:search-sort.best-match', icon: 'sort-relevance', asc: SortOptions.BestMatch },
    { id: 'popular', label: 'header:search-sort.popular-sort', icon: 'sort-popular', asc: SortOptions.Popular },
    { id: 'price', label: 'header:search-sort.price-sort', icon: 'sort-numeric', asc: SortOptions.PriceAsc, desc: SortOptions.PriceDesc },
    { id: 'name', label: 'header:search-sort.name-sort', icon: 'sort-alpha', asc: SortOptions.NameAsc, desc: SortOptions.NameDesc }
];

/** Which row a raw API value belongs to, and which way it points. */
export const findSortField = (value?: SortOptions) => {
    const field = DisplaySortFields.find((f) => f.asc === value || f.desc === value);
    if (!field) return null;
    return { field, direction: (field.desc === value ? 'desc' : 'asc') as SortDirection };
};

export const DisplaySortOptions: DisplaySortOptionsType[] = [
    { value: SortOptions.ClosingSoon, label: 'header:search-sort.closing-soon-sort', icon: 'sort-time' },
    { value: SortOptions.BestMatch, label: 'header:search-sort.best-match' },
    { value: SortOptions.Popular, label: 'header:search-sort.popular-sort' },
    { value: SortOptions.PriceAsc, label: 'header:search-sort.price-descending-sort', icon: 'sort-numeric' },
    { value: SortOptions.PriceDesc, label: 'header:search-sort.price-ascending-sort', icon: 'sort-numeric' },
    //  { value: SortOptions.DateDesc, label: ('header:search-sort.date-descending-sort') },
    // { value: SortOptions.DateAsc, label: ('header:search-sort.date-ascending-sort') },
    { value: SortOptions.NameAsc, label: 'header:search-sort.name-ascending-sort', icon: 'sort-alpha' },
    { value: SortOptions.NameDesc, label: 'header:search-sort.name-descending-sort', icon: 'sort-alpha' }
];
